import {FormattedMessage} from '@pinecast/i18n';
import * as React from 'react';

import ScreenReaderOnly from '../ScreenReaderOnly';

import {FormContext, useFormInstance} from './context';
import {
  errorTextOf,
  firstInvalidField,
  focusFirstInvalidField,
} from './focusFirstInvalid';
import {toFormApi} from './formApi';
import type {FormApi, FormValues} from './types';

const EMPTY_VALUES: FormValues = {};

/**
 * How long the error that a failed submit said stays in its live region. It
 * has been said by then, and text left in the region would be read as if it
 * were current, long after the user had fixed the field.
 */
export const ERROR_ANNOUNCEMENT_MS = 10_000;

// What a failed submit said. `count` is the number of the submit: it keys the
// node that holds the text, so that the same error, said again, is a new node.
type Announcement = {count: number; text: string};

type Props<TValues extends FormValues> = {
  /**
   * Keep `''` as a value when a field is cleared, instead of leaving it out of
   * the submitted values.
   */
  allowEmptyStrings?: boolean;
  children: React.ReactNode;
  /** Receives an imperative handle on the form, for resetting it and so on. */
  formApiRef?: React.RefObject<FormApi | null>;
  /**
   * Seeds the fields. Fields pick these up as they mount, and the form
   * re-seeds from them if they change while it is still untouched — so a form
   * rendered before its data arrives fills in once it does.
   *
   * This doubles as the form's declared shape: `onSubmit` receives values typed
   * from whatever is passed here, so reading a field that doesn't exist — or
   * whose name is spelled wrong — is a compile error instead of an `undefined`
   * that reaches the server.
   */
  initialValues?: TValues;
  /**
   * Runs once every field has validated. Returning a promise keeps the form in
   * its submitting state until it settles.
   *
   * `values` is typed from `initialValues`. That's an approximation worth
   * knowing about, in both directions: fields address the form by string name,
   * so a field can introduce a key `initialValues` never mentioned, and a field
   * that unmounts without `keepState` (or whose value the user cleared) leaves
   * its key out of the submitted values entirely. The type describes the form
   * as authored, not everything the store can hold.
   */
  onSubmit?: (formState: {values: NoInfer<TValues>}) => unknown;
} & Omit<React.FormHTMLAttributes<HTMLFormElement>, 'onSubmit'>;

/**
 * Wraps its children in a `<form>` and gives them a form to attach to. Fields
 * inside reach it with `useField`; anything else can use `useFieldState`,
 * `useFieldApi`, `useFormState`, or `useFormApi`.
 *
 * Submitting validates every field first, and `onSubmit` runs only if they all
 * pass. When they don't, focus moves to the first field with an error, and a
 * screen reader reads the field and its error.
 *
 * When focus is already on that field (the user pressed Enter in it), it does
 * not move and a screen reader reads nothing, so the form says the error
 * itself, from a live region (WCAG 4.1.3). The same goes for a failed submit
 * when no field with an error can take focus. The region is inside the form,
 * so a form in a dialog says it from within the dialog. It says an error one
 * time: not when focus moves, which is the announcement, not when a field is
 * left, and not when a submit goes through.
 *
 * The value type is inferred from `initialValues` and nowhere else — an
 * annotation on `onSubmit`'s parameter can't widen it back to `any`. A form
 * with no `initialValues` falls back to values keyed by string, as before.
 */
const Form = <TValues extends FormValues = FormValues>({
  allowEmptyStrings = false,
  children,
  formApiRef,
  initialValues,
  onSubmit,
  ...rest
}: Props<TValues>) => {
  // `onSubmit` is usually a fresh closure every render; the form is handed a
  // stable one that calls whichever is current.
  const onSubmitRef = React.useRef(onSubmit);
  onSubmitRef.current = onSubmit;

  const form = useFormInstance(initialValues ?? EMPTY_VALUES, values =>
    // The store is keyed by string name and isn't generic, so this is the one
    // place the untyped store meets the typed contract above.
    onSubmitRef.current?.({values: values as TValues}),
  );

  const context = React.useMemo(
    () => ({allowEmptyStrings, form}),
    [allowEmptyStrings, form],
  );

  React.useImperativeHandle(formApiRef, () => toFormApi(form), [form]);

  // Counts failed submits. Focus moves in an effect, once the fields have
  // rendered the errors that mark them invalid.
  const formRef = React.useRef<HTMLFormElement | null>(null);
  const [failedSubmits, setFailedSubmits] = React.useState(0);
  const [announcement, setAnnouncement] = React.useState<Announcement | null>(
    null,
  );
  React.useEffect(() => {
    if (!failedSubmits) {
      return;
    }
    // Focus that moves into a field makes a screen reader read the field with
    // its error, so that is the announcement, and the region stays empty. When
    // focus was in the field already, or had no field to go to, nothing is read
    // unless the region says it.
    const focused = focusFirstInvalidField(formRef.current);
    if (focused?.wasFocused === false) {
      return;
    }
    const field = focused?.field ?? firstInvalidField(formRef.current);
    if (field) {
      setAnnouncement({count: failedSubmits, text: errorTextOf(field)});
    }
  }, [failedSubmits]);

  // The text goes after a while, so that it is not left to be read as current.
  React.useEffect(() => {
    if (!announcement) {
      return;
    }
    const timer = setTimeout(
      () => setAnnouncement(null),
      ERROR_ANNOUNCEMENT_MS,
    );
    return () => clearTimeout(timer);
  }, [announcement]);

  return (
    <FormContext.Provider value={context}>
      <form
        {...rest}
        onSubmit={event => {
          event.preventDefault();
          event.stopPropagation();
          // What an earlier submit said is no longer true of this one.
          setAnnouncement(null);
          void form.handleSubmit().then(() => {
            if (!form.state.isValid) {
              setFailedSubmits(count => count + 1);
            }
          });
        }}
        ref={formRef}
      >
        {children}
        {/* Always rendered, and empty until there is something to say: a screen
            reader only announces a region that exists before its text does.
            It is an alert and nothing else (not `StatusMessage`, which also
            renders a `status` region that a form has no use for). */}
        <ScreenReaderOnly role="alert">
          {announcement && (
            <span key={announcement.count}>
              {announcement.text || (
                <FormattedMessage
                  description="Announced to screen readers after a form is submitted, when a field has an error that has no text of its own"
                  defaultMessage="A field is not valid."
                />
              )}
            </span>
          )}
        </ScreenReaderOnly>
      </form>
    </FormContext.Provider>
  );
};

export default Form;
